/**
 * Merge a hand-curated static list of events with the live, cron-fetched
 * feed for the same venue. Fixes the class of bug that hid The Love Inn's
 * Last Bus Home gig on 2026-09-26: the venue's carousel was in "static
 * items" mode, so the daily Headfirst-sync cron populated the DB but the
 * homepage never surfaced anything not manually added to the static list.
 *
 * Merge semantics:
 *  - Static wins on `sub` copy (editorial subtitles a human wrote)
 *  - Static wins on `imageUrl` when present (a hand-picked poster)
 *  - Fetched fills every gap the static list didn't cover
 *  - Deduplicate by extracting the Headfirst event ID from both sides'
 *    URLs; a numeric ID is the stable key (title strings drift as
 *    promoters tweak them)
 *  - Sort chronologically ascending
 *  - Drop events whose start is more than 6h in the past (so a Friday
 *    night gig ending at 04:00 Saturday still shows up when a fan checks
 *    the site at 01:30 Sat)
 *  - Cap at `limit` (default 12)
 *
 * Pure module (no React) so it can be tested from the root vitest suite,
 * see `tests/unit/carousel-merge.test.ts`.
 */

export type CarouselItem = {
  id: string;
  date: string;
  title: string;
  sub?: string;
  imageUrl?: string;
  href?: string;
  externalTicketsUrl?: string;
};

export type StaticItem = {
  date: string;
  title: string;
  sub?: string;
  imageUrl?: string;
  href: string;
};

export type FetchedEvent = {
  id: string;
  title: string;
  date: string | Date;
  imageUrl?: string | null;
  description?: string | null;
  externalTicketsUrl?: string | null;
};

// Headfirst listing URLs end in "-<numeric-id>": .../slug-158894 → "158894".
// Same shape appears in fetched event descriptions ("Tickets: URL")
// because runHeadfirstSync writes the URL into `description`.
export function extractHeadfirstId(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;
  const m = url.match(/-(\d+)(?:[/?#].*)?$/);
  return m ? m[1] : null;
}

function firstHeadfirstUrlFromText(text: string | null | undefined): string | null {
  if (!text) return null;
  const m = text.match(/https?:\/\/www\.headfirstbristol\.co\.uk\/[^\s]+/);
  return m ? m[0] : null;
}

function eventHeadfirstId(ev: FetchedEvent): string | null {
  return (
    extractHeadfirstId(firstHeadfirstUrlFromText(ev.description)) ??
    extractHeadfirstId(ev.externalTicketsUrl)
  );
}

const BARE_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

function toIsoDayString(v: string | Date): string {
  // Keep bare "YYYY-MM-DD" strings as-is so the past-cutoff can treat
  // them as "the whole day" rather than 00:00 UTC. A bare day gets
  // normalised only for chronological sorting.
  if (typeof v === "string" && BARE_DAY_RE.test(v)) return v;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/**
 * Past-cutoff logic:
 *  - Bare "YYYY-MM-DD" static items: keep visible until end of that
 *    local day (so a Sat gig curated as "2026-09-26" still shows at
 *    Sat afternoon 15:00 even without a time). We can't know the true
 *    start time, so treat "today's day" as still-current.
 *  - Timestamped items (fetched from the DB): six-hour grace window,
 *    so a club night starting 22:00 and ending 04:00 stays on the
 *    carousel when a fan opens the site at 01:30.
 *
 * The render layer's live-strip logic handles "currently on now"
 * precisely via `resolveEventEnd`; this cutoff is just for carousel
 * visibility.
 */
function isCurrentOrFuture(iso: string, now: Date): boolean {
  if (BARE_DAY_RE.test(iso)) {
    const nowDay = now.toISOString().slice(0, 10);
    return iso >= nowDay;
  }
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return t >= now.getTime() - 6 * 60 * 60 * 1000;
}

export function mergeCarouselItems(input: {
  staticItems: StaticItem[] | undefined;
  fetchedEvents: FetchedEvent[];
  limit?: number;
  now?: Date;
}): CarouselItem[] {
  const now = input.now ?? new Date();
  const limit = input.limit ?? 12;
  const staticItems = Array.isArray(input.staticItems) ? input.staticItems : [];

  // Build a map: headfirstId → static editorial data (sub, curated image).
  const staticById = new Map<string, StaticItem>();
  const staticWithoutId: StaticItem[] = [];
  for (const s of staticItems) {
    const id = extractHeadfirstId(s.href);
    if (id) staticById.set(id, s);
    else staticWithoutId.push(s);
  }

  const usedStaticIds = new Set<string>();
  const out: CarouselItem[] = [];

  // For each fetched event, produce a merged carousel item that prefers
  // editorial copy where present.
  for (const ev of input.fetchedEvents) {
    const hfId = eventHeadfirstId(ev);
    const editorial = hfId ? staticById.get(hfId) : null;
    if (editorial && hfId) usedStaticIds.add(hfId);
    const iso = toIsoDayString(ev.date);
    if (!isCurrentOrFuture(iso, now)) continue;
    const href =
      firstHeadfirstUrlFromText(ev.description) ??
      ev.externalTicketsUrl ??
      editorial?.href ??
      "";
    out.push({
      id: ev.id,
      date: iso,
      title: ev.title,
      sub: editorial?.sub,
      imageUrl: editorial?.imageUrl ?? ev.imageUrl ?? undefined,
      href: href || undefined,
      externalTicketsUrl: href || undefined,
    });
  }

  // Add any static-only items the fetched feed didn't cover (e.g. a
  // manually curated event without a Headfirst listing yet, or a static
  // item whose Headfirst URL we couldn't parse). These preserve the
  // venue admin's intent.
  for (const s of staticItems) {
    const id = extractHeadfirstId(s.href);
    if (id && usedStaticIds.has(id)) continue;
    if (!id && !staticWithoutId.includes(s)) continue;
    const iso = toIsoDayString(s.date);
    if (!isCurrentOrFuture(iso, now)) continue;
    out.push({
      id: `static-${s.href || s.title}`,
      date: iso,
      title: s.title,
      sub: s.sub,
      imageUrl: s.imageUrl,
      href: s.href,
      externalTicketsUrl: s.href,
    });
  }

  out.sort((a, b) => a.date.localeCompare(b.date));
  return out.slice(0, limit);
}
